import { styled } from '@/src/stitches.config'
import { CardImageButtonWrapperRow } from '@/src/modules/build-a-card/StyledComponents'
import Button from '@/src/common/components/marketing/buttons/Button'
import { CustomImageModal } from '@/src/modules/build-a-card/modals/CustomImageModal'
import { PredesignsModal } from '@/src/modules/build-a-card/modals/PredesignsModal'
import { FormErrorDisplay } from '@/src/modules/build-a-card/FormErrorDisplay'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import { useGiftQuantityContext } from '@/src/common/contexts/GiftQuantityContext'
import { useState } from 'react'
import { ValidationError } from 'yup'
import Image from 'next/image'
import {
    CUSTOM_IMAGE_TYPE,
    DEFAULT_IMAGE_TYPE,
    PREDESIGN_IMAGE_TYPE,
} from '@/src/modules/build-a-card/constants'

const StyledButtonsWrapper = styled(CardImageButtonWrapperRow, {
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
    '& button': {
        py: 13,
    },
    variants: {
        items: {
            1: {
                gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
            },
            2: {
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            },
            3: {
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            },
        },
    },
    defaultVariants: {
        items: 3,
    },
})

const StyledButton = styled(Button, {
    '& div': {
        fontSize: '.88rem',
    },
    '.adjust-icon': {
        mx: 'auto',
    },
})

const DefaultContentWrapper = styled('div', {
    width: '95%',
    display: 'flex',
    fd: 'column',
    jc: 'center',
    ai: 'center',
    gap: 10,
    '@sm': {
        width: '80%',
    },
})

const DefaultImageWrapper = styled('div', {
    width: '100%',
    margin: 'auto',
    '& img': {
        display: 'block',
        width: '100%',
        height: 'auto',
        objectFit: 'cover',
        br: 5,
        boxShadow: 'gray 0px 1px 5px -1px',
        aspectRatio: '3/2',
    },
})

// this should be a div, not a p tag for styling purposes
const DefaultButtonText = styled('div', {})

type TImageProps = {
    formErrors: ValidationError[]
}

const ImageSelectorButtons = ({ formErrors }: TImageProps) => {
    const {
        predesigns,
        setDesignImage,
        setCustomDesignObject,
        setDesignName,
        predesignUuid,
        setPredesignUuid,
        selectedImageType,
        setSelectedImageType,
        defaultPredesign,
        partnerData,
    } = useBasicGiftCardContext()
    const { giftState } = useGiftQuantityContext()
    const { isEditing } = giftState

    const [customImageModalIsOpen, setCustomImageModalIsOpen] =
        useState<boolean>(false)
    const [predesignsModalIsOpen, setPredesignsModalIsOpen] =
        useState<boolean>(false)

    const { default_preview_image, default_design_name, default_image_uuid } =
        defaultPredesign
    const showCustomImageUpload =
        !partnerData ||
        (partnerData?.programConfig?.show_custom_image_upload ?? false)

    return (
        <>
            <StyledButtonsWrapper items={!showCustomImageUpload ? 2 : 3}>
                {showCustomImageUpload && (
                    <>
                        <StyledButton
                            id={'upload-button'}
                            label={'Upload a photo'}
                            onClick={() => setCustomImageModalIsOpen(true)}
                            variant={
                                selectedImageType == CUSTOM_IMAGE_TYPE
                                    ? 'graySquareButtonVerticalActive'
                                    : 'graySquareButtonVertical'
                            }
                            iconType={'image'}
                            type={'button'}
                        />
                        <CustomImageModal
                            isOpen={customImageModalIsOpen}
                            setIsOpen={setCustomImageModalIsOpen}
                            setDesignImage={setDesignImage}
                            setDesignName={setDesignName}
                            setCustomDesignObject={setCustomDesignObject}
                            setPredesignUuid={setPredesignUuid}
                            setSelectedImageType={setSelectedImageType}
                        />
                    </>
                )}
                <StyledButton
                    id={'choose-design-button'}
                    label={'Choose a design'}
                    onClick={() => setPredesignsModalIsOpen(true)}
                    variant={
                        selectedImageType == PREDESIGN_IMAGE_TYPE
                            ? 'graySquareButtonVerticalActive'
                            : 'graySquareButtonVertical'
                    }
                    iconType={'upload'}
                    type={'button'}
                    style={{
                        marginBottom: 0,
                    }}
                />
                <StyledButton
                    id={'default-image-button'}
                    label={''}
                    onClick={() => {
                        setDesignImage(default_preview_image)
                        setDesignName(default_design_name)
                        setPredesignUuid(default_image_uuid)
                        setSelectedImageType(DEFAULT_IMAGE_TYPE)
                        setCustomDesignObject({})
                    }}
                    variant={
                        selectedImageType === DEFAULT_IMAGE_TYPE ||
                        (!selectedImageType && !isEditing)
                            ? 'graySquareButtonVerticalActive'
                            : 'graySquareButtonVertical'
                    }
                    type={'button'}
                >
                    <DefaultContentWrapper>
                        <DefaultImageWrapper>
                            <Image
                                alt={'default predesign image'}
                                height={280}
                                width={400}
                                src={default_preview_image}
                                priority={true}
                            />
                        </DefaultImageWrapper>
                        <DefaultButtonText>Default</DefaultButtonText>
                    </DefaultContentWrapper>
                </StyledButton>
            </StyledButtonsWrapper>
            <PredesignsModal
                isOpen={predesignsModalIsOpen}
                setIsOpen={setPredesignsModalIsOpen}
                setDesignImage={setDesignImage}
                setDesignName={setDesignName}
                setCustomDesignObject={setCustomDesignObject}
                predesignUuid={predesignUuid}
                setPredesignUuid={setPredesignUuid}
                predesigns={predesigns}
                headlineLabel={'wrapper designs'}
                setSelectedImageType={setSelectedImageType}
            />
            <FormErrorDisplay
                formErrors={formErrors}
                path={'designImage'}
                variant={'error-message'}
            />
        </>
    )
}

export default ImageSelectorButtons
